/**
 * One wall, one door (PRP-SKETCH-FIELD-3 H12, J14). Internally an opening on a
 * shared wall stays a linked pair (FIELD-1 F7), because both rooms' wall areas
 * deduct it. To the user it is one object: drawn once, selected once, listed once,
 * deleted once (`deleteOpening` already removes both copies while the rooms share
 * the wall).
 *
 * The one copy that stands for the pair (`canonicalOpening`):
 * - a door: the copy in the room it swings into (its `out` is false), so the
 *   drawing and the listing follow the swing ("Door to Hall, swings into Bedroom 2");
 * - anything else: the copy in the room drawn first (the earlier room).
 */
import { findRoom } from './geom';
import { adjacent, sharedMap } from './shared';
import type { Opening, OpeningKind, Plan, Room, SharedMap } from './types';

export interface OpeningRef { rid: string; oid: number }

/** The copy that stands for a linked pair on a shared wall (itself when unshared). */
export function canonicalOpening(plan: Plan, r: Room, o: Opening, M?: SharedMap): { r: Room; o: Opening } {
  const P = adjacent(plan, r, o, M ?? sharedMap(plan));
  if (!P) return { r, o };
  if (o.kind === 'door' && P.o.kind === 'door') {
    const mineIn = !o.door?.out, theirsIn = !P.o.door?.out;
    if (mineIn !== theirsIn) return mineIn ? { r, o } : P;
  }
  return plan.rooms.indexOf(r) <= plan.rooms.indexOf(P.r) ? { r, o } : P;
}

/** The room an opening id belongs to (opening ids are plan-unique). */
function locate(plan: Plan, oid: number): { r: Room; o: Opening } | null {
  for (const r of plan.rooms) {
    const o = r.openings.find((q) => q.id === oid);
    if (o) return { r, o };
  }
  return null;
}

/** Selection (J14): tapping either side of a shared opening selects this one id. */
export function resolveOpening(plan: Plan, rid: string, oid: number): OpeningRef | null {
  const r = findRoom(plan, rid);
  const o = r?.openings.find((q) => q.id === oid);
  if (!r || !o) return null;
  const c = canonicalOpening(plan, r, o);
  return { rid: c.r.id, oid: c.o.id };
}

/**
 * The room a door swings into: its own room when it swings in, the room across a
 * shared wall when it swings out, null when it swings outside (or is not a door).
 */
export function swingRoom(plan: Plan, oid: number): string | null {
  const at = locate(plan, oid);
  if (!at || at.o.kind !== 'door') return null;
  if (!at.o.door?.out) return at.r.id;
  return adjacent(plan, at.r, at.o)?.r.id ?? null;
}

export interface UserOpening extends OpeningRef {
  kind: OpeningKind;
  /** The other copy of a shared opening (internal; never shown as a second object). */
  partner?: OpeningRef;
  /** The room across the wall, when the opening is on a shared wall. */
  otherRid: string | null;
  /** A door: the room it swings into; null when it swings outside or is not a door. */
  swingRid: string | null;
  /** The room whose properties list it: a door under the room it swings into, others under their own. */
  listRid: string;
}

/** Every opening as the user sees it: each shared pair once (counts, lists, selection). */
export function userOpenings(plan: Plan): UserOpening[] {
  const M = sharedMap(plan);
  const out: UserOpening[] = [];
  for (const r of plan.rooms) {
    for (const o of r.openings) {
      const c = canonicalOpening(plan, r, o, M);
      if (c.o !== o) continue;
      const P = adjacent(plan, r, o, M);
      const swingRid = o.kind === 'door' ? (o.door?.out ? P?.r.id ?? null : r.id) : null;
      const u: UserOpening = { rid: r.id, oid: o.id, kind: o.kind, otherRid: P?.r.id ?? null, swingRid, listRid: swingRid ?? r.id };
      if (P) u.partner = { rid: P.r.id, oid: P.o.id };
      out.push(u);
    }
  }
  return out;
}

/** The kind's word in a listing. */
const KIND_WORD: Record<OpeningKind, string> = { door: 'Door', window: 'Window', missing: 'Opening' };

/** Room properties' line for one opening: "Door to Hall, swings into Bedroom 2". */
export function openingListText(plan: Plan, u: UserOpening): string {
  const name = (id: string | null) => (id ? findRoom(plan, id)?.name ?? '' : '');
  const word = KIND_WORD[u.kind];
  if (u.kind !== 'door') return u.otherRid ? `${word} to ${name(u.otherRid)}` : word;
  const across = u.otherRid ? (u.swingRid === u.rid ? u.otherRid : u.rid) : null;
  const to = across ? ` to ${name(across)}` : '';
  const swing = u.swingRid ? `swings into ${name(u.swingRid)}` : 'swings out';
  return `${word}${to}, ${swing}`;
}

/** The openings room `rid`'s properties list (J14), with their text. */
export function roomOpeningList(plan: Plan, rid: string): (UserOpening & { text: string })[] {
  return userOpenings(plan).filter((u) => u.listRid === rid).map((u) => ({ ...u, text: openingListText(plan, u) }));
}
